import React, { Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Medal, TrendingUp, CheckCircle2, Clock, Info, Crown } from 'lucide-react';
import { Activity, LeaderboardEntry, PointsConfig, defaultPointsConfig, pointsForFunding, tierForPoints } from './types';
interface LeaderboardProps {
  entries: LeaderboardEntry[];
  recentFundings: Activity[]; // only Funded activities count for points
  currentUser?: string;
  pointsConfig?: PointsConfig;
}
export function Leaderboard({
  entries,
  recentFundings,
  currentUser = 'You',
  pointsConfig = defaultPointsConfig
}: LeaderboardProps) {
  const sorted = [...entries].sort((a, b) => b.points - a.points);
  const me = sorted.find(e => e.loName === currentUser);
  const myRank = me ? sorted.findIndex(e => e.loName === currentUser) + 1 : null;
  const leader = sorted[0];
  const pointsToLeader = me && leader ? Math.max(0, leader.points - me.points) : 0;
  const myTier = me ? tierForPoints(me.points, pointsConfig) : null;
  // Build human-readable formula string. pointsPerThousand is points per $1k funded.
  // Express it as "1 pt per $Xk" when possible, else "N pts per $1k".
  const formulaText = (() => {
    const base = pointsConfig.basePoints;
    const ppt = pointsConfig.pointsPerThousand;
    if (ppt <= 0) return `${base} pts per funded loan`;
    if (ppt < 1) {
      const perK = Math.round(1 / ppt);
      return `${base} base + 1 pt per $${perK}k`;
    }
    return `${base} base + ${ppt} pts per $1k`;
  })();
  return <div className="space-y-4 h-full" data-id="element-150">
      {/* My standing card */}
      {me && <div className="relative overflow-hidden rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 via-slate-900 to-slate-900 p-5" data-id="element-151">
          <div className="flex items-center justify-between mb-3" data-id="element-152">
            <div className="flex items-center gap-2" data-id="element-153">
              <Trophy className="w-5 h-5 text-orange-400" data-id="element-154" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-orange-300" data-id="element-155">
                Your Standing
              </h3>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-950/60 px-2 py-1 rounded-full" data-id="element-156">
              This Week
            </span>
          </div>

          {myTier && <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-300" data-id="element-157">
              <Crown className="w-3.5 h-3.5" data-id="element-158" />
              <span className="text-[11px] font-bold uppercase tracking-wider" data-id="element-159">
                {myTier.name}
              </span>
            </div>}

          <div className="flex items-end justify-between" data-id="element-160">
            <div data-id="element-161">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-162">
                Rank
              </p>
              <p className="text-3xl font-bold font-display text-white" data-id="element-163">
                #{myRank}
                <span className="text-sm font-medium text-slate-500 ml-1" data-id="element-164">
                  of {sorted.length}
                </span>
              </p>
            </div>
            <div className="text-right" data-id="element-165">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-166">
                Points
              </p>
              <p className="text-3xl font-bold font-display text-gradient-orange" data-id="element-167">
                {me.points.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-id="element-168">
            <Stat icon={CheckCircle2} value={me.fundedCount} label="Funded" tone="emerald" data-id="element-169" />
            <Stat icon={Clock} value={me.pendingCount} label="Pending" tone="slate" data-id="element-170" />
            <Stat icon={TrendingUp} value={pointsToLeader === 0 ? '—' : `${pointsToLeader}`} label={pointsToLeader === 0 ? 'Leader' : 'To #1'} tone="orange" data-id="element-171" />
          </div>

          <div className="mt-4 flex items-start gap-2 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800" data-id="element-172">
            <Info className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" data-id="element-173" />
            <p className="text-[11px] text-slate-400 leading-snug" data-id="element-174">
              Points are awarded{' '}
              <span className="text-white font-semibold" data-id="element-175">
                only when a claimed loan funds
              </span>
              . Earn{' '}
              <span className="text-orange-300 font-semibold" data-id="element-176">
                {formulaText}
              </span>
              .
            </p>
          </div>
        </div>}

      {/* Leaderboard */}
      <div className="glass-panel rounded-2xl p-5" data-id="element-177">
        <div className="flex items-center justify-between mb-4" data-id="element-178">
          <h3 className="text-lg font-bold font-display text-white flex items-center gap-2" data-id="element-179">
            <Medal className="w-5 h-5 text-yellow-400" data-id="element-180" />
            Top Sharks
          </h3>
          <span className="flex h-2 w-2 relative" data-id="element-181">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-182" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-183" />
          </span>
        </div>

        <div className="space-y-2" data-id="element-184">
          {sorted.slice(0, 6).map((entry, idx) => {
          const rank = idx + 1;
          const isMe = entry.loName === currentUser;
          const max = sorted[0]?.points || 1;
          const pct = max > 0 ? entry.points / max * 100 : 0;
          const tier = tierForPoints(entry.points, pointsConfig);
          return <motion.div key={entry.loName} layout className={`relative overflow-hidden rounded-xl border px-3 py-2.5 ${isMe ? 'border-orange-500/40 bg-orange-500/5' : 'border-slate-800 bg-slate-900/40'}`} data-id="element-185">
                {/* Progress bar background */}
                <div className={`absolute inset-y-0 left-0 ${rank === 1 ? 'bg-gradient-to-r from-yellow-500/20 to-yellow-500/0' : rank === 2 ? 'bg-gradient-to-r from-slate-400/15 to-slate-400/0' : rank === 3 ? 'bg-gradient-to-r from-orange-700/15 to-orange-700/0' : 'bg-gradient-to-r from-slate-700/10 to-slate-700/0'}`} style={{
              width: `${pct}%`
            }} data-id="element-186" />

                <div className="relative flex items-center justify-between gap-3" data-id="element-187">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-188">
                    <RankBadge rank={rank} data-id="element-189" />
                    <div className="min-w-0" data-id="element-190">
                      <p className={`text-sm font-semibold truncate ${isMe ? 'text-orange-200' : 'text-white'}`} data-id="element-191">
                        {entry.loName}
                        {isMe && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-orange-400" data-id="element-192">
                            You
                          </span>}
                      </p>
                      <p className="text-[11px] text-slate-400" data-id="element-193">
                        <span className="text-yellow-400/80 font-semibold" data-id="element-194">
                          {tier.name}
                        </span>
                        <span className="text-slate-600" data-id="element-195"> • </span>
                        {entry.fundedCount} funded
                        {entry.pendingCount > 0 && <span className="text-slate-500" data-id="element-196">
                            {' '}
                            • {entry.pendingCount} pending
                          </span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0" data-id="element-197">
                    <p className="text-base font-bold font-display text-white" data-id="element-198">
                      {entry.points.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold" data-id="element-199">
                      pts
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Recent fundings strip */}
      <div className="glass-panel rounded-2xl p-5" data-id="element-200">
        <h3 className="text-sm font-bold font-display text-white flex items-center gap-2 mb-3" data-id="element-201">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-202" />
          Recent Fundings
        </h3>
        <div className="space-y-2" data-id="element-203">
          <AnimatePresence data-id="element-204">
            {recentFundings.length === 0 && <p className="text-xs text-slate-500 italic" data-id="element-205">
                No funded loans yet today — claim one and close it to earn
                points.
              </p>}
            {recentFundings.slice(0, 4).map(a => <motion.div key={a.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0
          }} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-slate-800/60 last:border-0" data-id="element-206">
                <div className="min-w-0" data-id="element-207">
                  <p className="font-semibold text-slate-200 truncate" data-id="element-208">
                    {a.loName}
                  </p>
                  <p className="text-slate-500 truncate" data-id="element-209">
                    funded {a.borrowerName}
                  </p>
                </div>
                <div className="text-right shrink-0" data-id="element-210">
                  <p className="text-emerald-400 font-bold" data-id="element-211">
                    +{pointsForFunding(a.amount, pointsConfig)} pts
                  </p>
                  <p className="text-[10px] text-slate-500" data-id="element-212">
                    ${(a.amount / 1000).toFixed(0)}k
                  </p>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}
function RankBadge({
  rank
}: {
  rank: number;
}) {
  const styles = rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-950 shadow-lg shadow-yellow-500/20' : rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-slate-900' : rank === 3 ? 'bg-gradient-to-br from-orange-600 to-orange-800 text-orange-100' : 'bg-slate-800 text-slate-400 border border-slate-700';
  return <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold font-display ${styles}`} data-id="element-213">
      {rank}
    </div>;
}
function Stat({
  icon: Icon,
  value,
  label,
  tone
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  value: number | string;
  label: string;
  tone: 'emerald' | 'slate' | 'orange';
}) {
  const colorMap = {
    emerald: 'text-emerald-400',
    slate: 'text-slate-300',
    orange: 'text-orange-400'
  };
  return <div className="rounded-lg bg-slate-950/40 border border-slate-800 p-2" data-id="element-214">
      <Icon className={`w-3.5 h-3.5 mx-auto mb-0.5 ${colorMap[tone]}`} data-id="element-215" />
      <p className={`text-base font-bold font-display ${colorMap[tone]}`} data-id="element-216">
        {value}
      </p>
      <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold" data-id="element-217">
        {label}
      </p>
    </div>;
}